import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EnvironmentOutlined } from "@ant-design/icons";
import { Button, Card, Empty, Tag } from "antd";
import Heading from "../../components/Heading";
import "../SearchResults/shared.scss";
import "./style.scss";

const PincodeSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [serviceRows, setServiceRows] = useState([]);
  const [cityText, setCityText] = useState("");
  const [stateText, setStateText] = useState("");

  useEffect(() => {
    const { pincodeData, pincode } = location.state || {};

    setServiceRows([]);
    setCityText("");
    setStateText("");

    if (!pincodeData) return;

    const actualData = pincodeData?.data || {};
    const serviceDetails = Array.isArray(actualData.serviceDetails)
      ? actualData.serviceDetails
      : [];

    setCityText(actualData.city?.toUpperCase() || "");
    setStateText(actualData.state?.toUpperCase() || "");

    const mappedData = serviceDetails.map((item, index) => ({
      key: `${pincode}-${index}`,
      hubName: item.hub_name || "Mapped Hub",
      serviceType: item.serviceType?.toUpperCase() || "SERVICE",
      isServiceable: Boolean(item.serviceable),
    }));

    setServiceRows(mappedData);
  }, [location.state]);

  const pincode = location.state?.pincode || "";
  const locationLabel = [cityText, stateText].filter(Boolean).join(", ");
  const serviceableCount = serviceRows.filter((item) => item.isServiceable).length;
  const mapUrl = pincode
    ? `/pincode-map?pincode=${encodeURIComponent(pincode)}`
    : "/pincode-map";

  const groupedHubCards = Object.values(
    serviceRows.reduce((acc, row) => {
      const hubKey = row.hubName || "Mapped Hub";
      if (!acc[hubKey]) {
        acc[hubKey] = {
          hubName: hubKey,
          services: [],
          serviceableCount: 0,
        };
      }

      acc[hubKey].services.push({
        key: row.key,
        serviceType: row.serviceType,
        isServiceable: row.isServiceable,
      });

      if (row.isServiceable) {
        acc[hubKey].serviceableCount += 1;
      }

      return acc;
    }, {})
  );

  const goBackToPrevScreen = () => {
    if (window.history.length <= 1) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="global-search-page pincode-search-page">
      <div className="p-2-tab">
        <Heading
          title="Pincode Search"
          showBack={true}
          goBack={goBackToPrevScreen}
        />
      </div>

      <div className="global-search-shell">
        <Card className="global-search-panel global-search-header-card" bordered={false}>
          <div className="global-search-title-row">
            <div className="global-search-title-block">
              <h1>{pincode || "Pincode Search"}</h1>
              {locationLabel ? <p>{locationLabel}</p> : null}
            </div>
            {groupedHubCards.length ? (
              <span className="global-search-badge">{groupedHubCards.length} hubs</span>
            ) : null}
          </div>
          <div className="global-search-actions">
            <Button type="primary" onClick={() => navigate(mapUrl)}>
              <EnvironmentOutlined /> Open Pincode Map
            </Button>
            <Button onClick={goBackToPrevScreen}>Back</Button>
          </div>
        </Card>

        <div className="global-search-metric-grid">
          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">Hubs</div>
            <div className="global-search-metric-value">{groupedHubCards.length}</div>
          </Card>

          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">Services</div>
            <div className="global-search-metric-value">{serviceRows.length}</div>
          </Card>

          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">Serviceable</div>
            <div className="global-search-metric-value">{serviceableCount}</div>
          </Card>
        </div>

        {groupedHubCards.length ? (
          <div className="global-search-result-grid">
            {groupedHubCards.map((hub, index) => {
              const isAllServiceable =
                hub.services.length > 0 && hub.serviceableCount === hub.services.length;
              const isNoneServiceable = hub.serviceableCount === 0;
              const statusClass = isAllServiceable
                ? "serviceable"
                : isNoneServiceable
                  ? "non-serviceable"
                  : "mixed";
              const statusLabel = isAllServiceable
                ? "Serviceable"
                : isNoneServiceable
                  ? "Not serviceable"
                  : "Partially serviceable";

              return (
                <Card
                  key={`${hub.hubName}-${index}`}
                  className="global-search-result-card pincode-result-card"
                  bordered={false}
                >
                  <div className="global-search-card-head">
                    <div>
                      <h2 className="global-search-card-title">
                        {hub.hubName || `Hub ${index + 1}`}
                      </h2>
                      <p className="global-search-card-subtitle">
                        {hub.services.length} services
                      </p>
                    </div>
                    <Tag className={`pincode-result-status ${statusClass}`}>{statusLabel}</Tag>
                  </div>

                  <div className="pincode-service-chip-row">
                    {hub.services.map((service, serviceIndex) => (
                      <Tag
                        key={`${service.key}-${serviceIndex}`}
                        className={`pincode-service-chip ${
                          service.isServiceable ? "serviceable" : "non-serviceable"
                        }`}
                      >
                        {service.serviceType}
                      </Tag>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="global-search-panel global-search-empty" bordered={false}>
            <Empty
              description={
                pincode
                  ? `No mapped services were returned for ${pincode}.`
                  : "Run a pincode search from global search to view results."
              }
            />
            <div className="global-search-actions global-search-actions-tight">
              {pincode ? (
                <Button type="primary" onClick={() => navigate(mapUrl)}>
                  Open Pincode Map
                </Button>
              ) : null}
              <Button onClick={goBackToPrevScreen}>Back</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PincodeSearch;
